import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { formsApi } from '@/api/forms';
import { queryKeys } from '@/lib/queryKeys';
import { formatDate } from '@/lib/formatDate';
import { cn } from '@/lib/cn';
import { Plus, Copy, Check } from 'lucide-react';
import { useState } from 'react';

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={(e) => {
        e.preventDefault();
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
      className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600"
    >
      {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
      {copied ? 'Copiado' : 'Copiar'}
    </button>
  );
}

export function FormsListPage() {
  const { data, isLoading } = useQuery({
    queryKey: queryKeys.forms.all,
    queryFn: () => formsApi.list(),
  });

  const forms = data?.forms ?? [];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Formularios</h1>
        <Link
          to="/forms/new"
          className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg font-medium hover:bg-primary-dark transition-colors"
        >
          <Plus className="h-4 w-4" />
          Novo Formulario
        </Link>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white rounded-xl border border-gray-200 p-5 animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-1/3 mb-2" />
              <div className="h-3 bg-gray-100 rounded w-1/2" />
            </div>
          ))}
        </div>
      ) : forms.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <p className="text-gray-500 mb-4">Nenhum formulario ainda.</p>
          <Link to="/forms/new" className="text-primary hover:underline font-medium">
            Crie seu primeiro formulario
          </Link>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100 text-left text-sm text-gray-500">
                <th className="px-5 py-3 font-medium">Nome</th>
                <th className="px-5 py-3 font-medium">Endpoint</th>
                <th className="px-5 py-3 font-medium">Envios</th>
                <th className="px-5 py-3 font-medium">Status</th>
                <th className="px-5 py-3 font-medium">Criado em</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {forms.map((form) => (
                <tr key={form.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-5 py-3">
                    <Link to={`/forms/${form.id}`} className="font-medium text-gray-900 hover:text-primary">
                      {form.name}
                    </Link>
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      <code className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">/f/{form.slug}</code>
                      <CopyButton text={`${window.location.origin}/f/${form.slug}`} />
                    </div>
                  </td>
                  <td className="px-5 py-3 text-sm text-gray-600">{form.submissionCount ?? 0}</td>
                  <td className="px-5 py-3">
                    <span className={cn(
                      'inline-block px-2 py-0.5 rounded-full text-xs font-medium',
                      form.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700',
                    )}>
                      {form.isActive ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-sm text-gray-500">{formatDate(form.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
